import type { RouteLocationRaw } from 'vue-router'
import {
  type BeforeFetchContext,
  type OnFetchErrorContext,
  useFetch as useVueFetch
} from '@vueuse/core'
import FetchError from '@/FetchError'
import useApp from '@/composables/app'
import { useAuthStore } from '@/stores/auth'

const navigateTo = async (location: RouteLocationRaw) => {
  const { default: router } = await import('@/router')
  return router.push(location)
}

export const useFetch = () => {
  const fetch = <T>(url: string, fetchOptions: RequestInit = {}) => {
    const { apiBaseUrl, storagePrefix } = useApp()
    const auth = useAuthStore()

    return useVueFetch<T>(`${apiBaseUrl}/${url}`, {
      beforeFetch: ({ options }: BeforeFetchContext) => {
        let localeHeader: string | null = null

        if (typeof window !== 'undefined' && window.localStorage) {
          try {
            localeHeader = window.localStorage.getItem(`${storagePrefix}lang`)
          } catch {
            localeHeader = null
          }
        }

        options.headers = {
          Accept: 'application/json',
          Authorization: `Bearer ${auth.token}`,
          ...options.headers,
          ...(localeHeader ? { Locale: localeHeader } : {})
        }

        return { options: { ...options, ...fetchOptions } }
      },

      updateDataOnError: true,

      onFetchError(context: OnFetchErrorContext<T, FetchError>) {
        if (context.error) {
          if (context.response?.status === 401) {
            auth.logout()
            setTimeout(() => {
              void navigateTo({ name: 'login' })
            })
            return context
          }

          const payload = context.data as {
            errors?: Record<string, unknown>
            message?: string
          } | null

          if (payload?.message) {
            const fieldErrors = (payload.errors as Record<string, string[]> | undefined) ?? null

            context.error = new FetchError(
              payload.message,
              fieldErrors,
              context.response?.status ?? null
            )
          } else if (context.response?.status === 500) {
            context.error = new FetchError('Unexpected server error. Please try again.', null, 500)
          }
        }

        return context
      }
    })
  }

  return { fetch }
}
